import CreateCompanyModal from '@/components/features/WorkOrders/forms/CreateCompanyModal';
import { Badge } from '@/components/ui/shadcn/badge';
import { Button } from '@/components/ui/shadcn/button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator } from '@/components/ui/shadcn/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/shadcn/popover';
import { useDebounce } from '@/hooks/use-debounce';
import { useTranslations } from '@/hooks/useTranslations';
import { cn } from '@/lib/utils';
import axios from 'axios';
import { Building2, Check, ChevronDown, Loader2, Plus, TriangleAlert, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export interface CompanyOption {
    id: string;
    name: string;
    company_code?: string | null;
    country?: string | null;
}

/** What the API hands back — `uuid` on the model, `id` once it has passed through a form. */
type CompanyInput = Partial<CompanyOption> & { uuid?: string };

interface RegistrySuggestion {
    id?: string;
    code: string;
    name: string;
    address_label?: string | null;
}

interface CompanySelectorBase {
    /** Ids surfaced under a "Suggested" heading above the search results. */
    suggestedIds?: string[];
    /**
     * Companies the caller already has in hand. Saves a round-trip when resolving
     * the current value into a label, and seeds the list before the first search.
     */
    seedCompanies?: CompanyInput[];
    disabled?: boolean;
    className?: string;
    /**
     * 'field'  — fills the width of a form row, always bordered.
     * 'inline' — full width, but drops the border and background once a company is
     *            picked so it reads as a value rather than an input.
     */
    variant?: 'field' | 'inline';
    /** Offer the "Create new company" footer, which opens the full create form in a sheet. */
    allowCreate?: boolean;
    /**
     * Offer companies found in the state registry but not yet in the CRM. Picking one opens
     * the create form pre-filled from the registry (or imports it directly when allowCreate is off).
     */
    allowRegistry?: boolean;
    /**
     * Copy overrides. Each falls back to a generic `common.company_select.*` string, so a
     * domain can say "Search suppliers…" without the component knowing about suppliers.
     */
    labels?: {
        placeholder?: string;
        search?: string;
        empty?: string;
        suggestedHeading?: string;
        companiesHeading?: string;
        clear?: string;
        create?: string;
        /** Multi mode only: shown as "3 {selected}" once more than one is picked. */
        selected?: string;
    };
}

export interface CompanySelectorSingleProps extends CompanySelectorBase {
    multiple?: false;
    value?: string | null;
    onValueChange: (value: string | null) => void;
    /** Fires alongside onValueChange with the whole record, for callers that need the code or country. */
    onCompanyResolved?: (company: CompanyOption | null) => void;
    /** Render the inline × that clears the selection. */
    allowClear?: boolean;
}

export interface CompanySelectorMultipleProps extends CompanySelectorBase {
    multiple: true;
    values: string[];
    onValuesChange: (values: string[]) => void;
    /** Fires alongside onValuesChange with the whole records. */
    onCompaniesResolved?: (companies: CompanyOption[]) => void;
}

export type CompanySelectorProps = CompanySelectorSingleProps | CompanySelectorMultipleProps;

/** Stable identity for the optional list props, so the effects below don't re-run on every render. */
const NO_SEEDS: CompanyInput[] = [];
const NO_IDS: string[] = [];

const normalizeCompany = (raw: CompanyInput): CompanyOption => ({
    id: String(raw.id ?? raw.uuid ?? ''),
    name: String(raw.name ?? ''),
    company_code: raw.company_code ? String(raw.company_code) : '',
    country: raw.country ? String(raw.country) : '',
});

/**
 * Company picker built to the same contract as PersonSelect: a searchable popover over a
 * list, single or multi, with caller-supplied copy.
 *
 * Unlike PersonSelect the list is not prop-fed — companies run to five figures, so the
 * search hits `/api/companies` and, past two characters, the state registry as well.
 * Picking a registry hit opens the full create form (the same one as /companies/create)
 * in a sheet, pre-filled with what the registry knows, so the user completes the rest
 * before the company exists. "Create new company" opens the same sheet, empty.
 */
export function CompanySelector(props: CompanySelectorProps) {
    const {
        suggestedIds = NO_IDS,
        seedCompanies = NO_SEEDS,
        disabled = false,
        className,
        variant = 'field',
        allowCreate = true,
        allowRegistry = true,
        labels,
    } = props;
    const isMultiple = props.multiple === true;

    const { t } = useTranslations();

    const placeholder = labels?.placeholder ?? t('common.company_select.placeholder', 'Select a company');
    const searchLabel = labels?.search ?? t('common.company_select.search', 'Search by name or code…');
    const emptyLabel = labels?.empty ?? t('common.company_select.empty', 'No companies found.');
    const suggestedHeading = labels?.suggestedHeading ?? t('common.company_select.suggested', 'Suggested');
    const companiesHeading = labels?.companiesHeading ?? t('common.company_select.companies', 'Companies');
    const clearLabel = labels?.clear ?? t('common.company_select.clear', 'Clear selection');
    const createLabel = labels?.create ?? t('common.company_select.create', 'Create new company');
    const selectedWord = labels?.selected ?? t('common.company_select.selected', 'selected');

    const [open, setOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const [isImporting, setIsImporting] = useState(false);
    const [registryFailed, setRegistryFailed] = useState(false);
    const [isCreateOpen, setIsCreateOpen] = useState(false);
    /** Set when the create form was opened from a registry hit, so it opens pre-filled. */
    const [createRegistryCode, setCreateRegistryCode] = useState<string | null>(null);
    const [crmOptions, setCrmOptions] = useState<CompanyOption[]>([]);
    const [registryOptions, setRegistryOptions] = useState<RegistrySuggestion[]>([]);
    /** Everything we have ever seen, so a picked company keeps its label after the list is cleared. */
    const [known, setKnown] = useState<Record<string, CompanyOption>>({});

    const debouncedSearch = useDebounce(searchTerm, 300);

    // Selection state, normalised so the render path below is mode-agnostic.
    const singleValue = isMultiple ? null : ((props as CompanySelectorSingleProps).value ?? null);
    const multiValues = useMemo(() => (isMultiple ? (props as CompanySelectorMultipleProps).values : []), [isMultiple, props]);
    const selectedIds = useMemo(() => (isMultiple ? multiValues : singleValue ? [singleValue] : []), [isMultiple, multiValues, singleValue]);
    const hasSelection = selectedIds.length > 0;

    const remember = useCallback((companies: CompanyOption[]) => {
        setKnown((previous) => {
            const next = { ...previous };
            companies.forEach((company) => {
                if (company.id) {
                    next[company.id] = company;
                }
            });

            return next;
        });
    }, []);

    // Seeds are props, so they land in the lookup without waiting on a request.
    const seeded = useMemo(() => seedCompanies.map(normalizeCompany).filter((company) => company.id), [seedCompanies]);
    const lookup = useMemo(() => {
        const map: Record<string, CompanyOption> = { ...known };
        seeded.forEach((company) => {
            map[company.id] = company;
        });

        return map;
    }, [known, seeded]);

    const selectedCompanies = selectedIds.map((id) => lookup[id]).filter(Boolean);
    const selectedSingle = singleValue ? (lookup[singleValue] ?? null) : null;

    // Resolve ids we have no label for — a form reopened on a saved value, most often.
    const requestedIds = useRef(new Set<string>());
    useEffect(() => {
        const missing = selectedIds.filter((id) => id && !lookup[id] && !requestedIds.current.has(id));

        if (missing.length === 0) {
            return;
        }

        missing.forEach((id) => requestedIds.current.add(id));

        let cancelled = false;
        void (async () => {
            const resolved = await Promise.all(
                missing.map(async (id) => {
                    try {
                        const response = await axios.get('/api/companies', { params: { id, limit: 1 } });
                        const raw = response.data?.companies?.[0];

                        return raw ? normalizeCompany(raw as CompanyInput) : null;
                    } catch {
                        // Leave it unresolved — the id just won't render a name.
                        return null;
                    }
                }),
            );

            if (!cancelled) {
                remember(resolved.filter((company): company is CompanyOption => company !== null));
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [selectedIds, lookup, remember]);

    // Search on open, and on every settled keystroke after that.
    useEffect(() => {
        if (!open) {
            setSearchTerm('');
            setCrmOptions([]);
            setRegistryOptions([]);
            setRegistryFailed(false);

            return;
        }

        let cancelled = false;

        void (async () => {
            setIsSearching(true);
            setRegistryFailed(false);

            try {
                const query = debouncedSearch.trim();
                const doRegistry = allowRegistry && query.length >= 2;

                const localResponse = await axios.get('/api/companies', {
                    params: { search: query || undefined, limit: doRegistry ? 25 : 10, page: 1 },
                });
                if (cancelled) return;

                const localList = ((localResponse.data?.companies ?? []) as CompanyInput[])
                    .map(normalizeCompany)
                    .filter((company) => company.id && company.name);

                setCrmOptions(localList);
                remember(localList);
                setIsSearching(false);

                if (!doRegistry) {
                    setRegistryOptions([]);

                    return;
                }

                try {
                    const registryResponse = await axios.get('/api/company/suggest', { params: { q: query, limit: 15 }, timeout: 4000 });
                    if (cancelled) return;

                    const localCodes = new Set(localList.map((company) => company.company_code).filter(Boolean));
                    setRegistryOptions(
                        (Array.isArray(registryResponse.data) ? registryResponse.data : []).filter(
                            (suggestion: RegistrySuggestion) => suggestion.code && suggestion.name && !localCodes.has(suggestion.code),
                        ),
                    );
                } catch {
                    // The CRM results still stand on their own; say so rather than failing the whole list.
                    if (!cancelled) {
                        setRegistryFailed(true);
                        setRegistryOptions([]);
                    }
                }
            } catch {
                if (!cancelled) {
                    setCrmOptions([]);
                    setRegistryOptions([]);
                    setIsSearching(false);
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [open, debouncedSearch, allowRegistry, remember]);

    const select = (company: CompanyOption) => {
        remember([company]);

        if (isMultiple) {
            const { onValuesChange, onCompaniesResolved } = props as CompanySelectorMultipleProps;
            // Stay open: picking several companies in a row is the point of multi mode.
            const next = multiValues.includes(company.id) ? multiValues.filter((id) => id !== company.id) : [...multiValues, company.id];

            onValuesChange(next);
            onCompaniesResolved?.(next.map((id) => (id === company.id ? company : lookup[id])).filter(Boolean));

            return;
        }

        const { onValueChange, onCompanyResolved } = props as CompanySelectorSingleProps;
        const cleared = company.id === singleValue;

        onValueChange(cleared ? null : company.id);
        onCompanyResolved?.(cleared ? null : company);
        setOpen(false);
    };

    const clear = () => {
        if (isMultiple) {
            const { onValuesChange, onCompaniesResolved } = props as CompanySelectorMultipleProps;
            onValuesChange([]);
            onCompaniesResolved?.([]);

            return;
        }

        const { onValueChange, onCompanyResolved } = props as CompanySelectorSingleProps;
        onValueChange(null);
        onCompanyResolved?.(null);
    };

    /**
     * Opens the create form pre-filled from the registry. The registry only knows name,
     * code, VAT and address — type and the rest are the user's to fill in, so the record
     * lands complete rather than as a stub.
     */
    const openCreateFromRegistry = (suggestion: RegistrySuggestion) => {
        setOpen(false);
        setCreateRegistryCode(suggestion.code);
        // Same handoff as the "Create new company" footer: let the popover finish
        // unmounting before the sheet takes over the body's pointer-events.
        window.setTimeout(() => setIsCreateOpen(true), 0);
    };

    /** Fallback for callers that do not offer the create form: import the registry record as-is. */
    const importFromRegistry = async (suggestion: RegistrySuggestion) => {
        setIsImporting(true);

        try {
            const response = await axios.post('/api/company/import', { code: suggestion.code });
            const raw = response.data?.company;

            if (!raw) {
                throw new Error('No company in response');
            }

            const imported = normalizeCompany(raw as CompanyInput);
            setCrmOptions((previous) => [imported, ...previous.filter((company) => company.id !== imported.id)]);
            setRegistryOptions((previous) => previous.filter((entry) => entry.code !== suggestion.code));
            select(imported);
        } catch {
            // Keep the registry row on screen; the user can retry.
        } finally {
            setIsImporting(false);
        }
    };

    const handleCompanyCreated = (raw: CompanyInput) => {
        const created = normalizeCompany(raw);
        setCrmOptions((previous) => [created, ...previous.filter((company) => company.id !== created.id)]);
        setRegistryOptions((previous) => previous.filter((entry) => entry.code !== created.company_code));
        select(created);
        closeCreate();
    };

    const closeCreate = () => {
        setIsCreateOpen(false);
        setCreateRegistryCode(null);
    };

    const allowClear = isMultiple ? true : ((props as CompanySelectorSingleProps).allowClear ?? true);
    const showClear = allowClear && hasSelection && !disabled;
    const chromeless = variant === 'inline' && hasSelection;

    const suggested = crmOptions.filter((company) => suggestedIds.includes(company.id));
    const rest = crmOptions.filter((company) => !suggestedIds.includes(company.id));

    const renderItem = (company: CompanyOption) => (
        <CommandItem key={company.id} value={`crm-${company.id}`} onSelect={() => select(company)} className="cursor-pointer gap-2">
            <Building2 className="text-muted-foreground h-4 w-4 shrink-0" />
            {/* The code sits beside the name once there is room for both; below sm it
                wraps, so the name keeps the width it needs to stay distinguishable. */}
            <div className="flex min-w-0 flex-1 flex-col gap-1 sm:flex-row sm:items-center sm:justify-between sm:gap-2">
                <span className="min-w-0 truncate text-sm">{company.name}</span>
                {company.company_code ? (
                    <Badge variant="secondary" className="text-muted-foreground shrink-0 text-xs font-normal">
                        {company.company_code}
                    </Badge>
                ) : null}
            </div>
            {selectedIds.includes(company.id) ? <Check className="text-primary h-4 w-4 shrink-0" /> : null}
        </CommandItem>
    );

    /**
     * The trigger's leading badge, a 24px circle either way so the field keeps its
     * shape whether or not a company is picked. Dashed reads as an empty seat, the
     * same cue PersonSelect uses for an unassigned person.
     */
    const triggerBadge = (empty: boolean) => (
        <span
            className={cn(
                'text-muted-foreground flex h-6 w-6 shrink-0 items-center justify-center rounded-full',
                empty ? 'border-muted-foreground/40 border border-dashed' : 'bg-muted',
            )}
        >
            <Building2 className="h-3.5 w-3.5" />
        </span>
    );

    return (
        <div className="relative flex w-full items-center">
            {/* modal: keeps the list clickable when this sits inside a Dialog or
                Drawer, which makes the rest of the page inert. */}
            <Popover modal open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <Button
                        type="button"
                        variant={chromeless ? 'ghost' : 'outline'}
                        role="combobox"
                        aria-expanded={open}
                        disabled={disabled || isImporting}
                        className={cn(
                            // Sized off the 24px badge, matching PersonSelect so a company
                            // field and an owner field sitting side by side line up.
                            'h-auto w-full justify-start gap-2 py-1.5 pr-9 pl-1.5 font-normal',
                            !hasSelection && 'text-muted-foreground',
                            className,
                        )}
                    >
                        {isImporting ? (
                            <>
                                <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
                                <span className="truncate text-sm">{t('common.company_select.importing', 'Importing from registry…')}</span>
                            </>
                        ) : !hasSelection ? (
                            <>
                                {triggerBadge(true)}
                                <span className="truncate text-sm">{placeholder}</span>
                            </>
                        ) : isMultiple && selectedIds.length > 1 ? (
                            <>
                                {triggerBadge(false)}
                                <span className="truncate text-sm">{`${selectedIds.length} ${selectedWord}`}</span>
                            </>
                        ) : (
                            <>
                                {triggerBadge(false)}
                                <span className="min-w-0 truncate text-sm">
                                    {(isMultiple ? selectedCompanies[0]?.name : selectedSingle?.name) ?? ''}
                                </span>
                                {(isMultiple ? selectedCompanies[0]?.company_code : selectedSingle?.company_code) ? (
                                    <Badge variant="secondary" className="text-muted-foreground shrink-0 text-xs font-normal">
                                        {isMultiple ? selectedCompanies[0]?.company_code : selectedSingle?.company_code}
                                    </Badge>
                                ) : null}
                            </>
                        )}
                    </Button>
                </PopoverTrigger>

                <PopoverContent
                    className="w-[var(--radix-popover-trigger-width)] p-0"
                    align="start"
                    onOpenAutoFocus={(event) => event.preventDefault()}
                >
                    {/* The search row is boxed rather than the primitive's bottom-ruled
                        header, so it reads as a field sitting above the list. */}
                    <Command
                        shouldFilter={false}
                        className={cn(
                            '[&_[data-slot=command-input-wrapper]]:border-input [&_[data-slot=command-input-wrapper]]:m-2',
                            '[&_[data-slot=command-input-wrapper]]:h-10 [&_[data-slot=command-input-wrapper]]:rounded-md [&_[data-slot=command-input-wrapper]]:border',
                            '[&_[data-slot=command-input-wrapper]]:focus-within:border-primary [&_[data-slot=command-input-wrapper]]:focus-within:ring-primary/20 [&_[data-slot=command-input-wrapper]]:focus-within:ring-[3px]',
                        )}
                    >
                        {/* No autoFocus: the list is already populated when the popover
                            opens, so raising the keyboard would cover it. */}
                        <CommandInput placeholder={searchLabel} value={searchTerm} onValueChange={setSearchTerm} />

                        <CommandList className="max-h-72">
                            {isSearching ? (
                                <div className="text-muted-foreground flex items-center justify-center gap-2 py-6 text-sm">
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                    {t('common.company_select.searching', 'Searching…')}
                                </div>
                            ) : null}

                            {/* The list is populated on open, so an empty one means there is
                                genuinely nothing to show — not that the user has yet to type. */}
                            {!isSearching && crmOptions.length === 0 && registryOptions.length === 0 ? (
                                <CommandEmpty>{emptyLabel}</CommandEmpty>
                            ) : null}

                            {!isSearching && suggested.length > 0 ? (
                                <CommandGroup heading={suggestedHeading}>{suggested.map(renderItem)}</CommandGroup>
                            ) : null}

                            {!isSearching && rest.length > 0 ? <CommandGroup heading={companiesHeading}>{rest.map(renderItem)}</CommandGroup> : null}

                            {!isSearching && registryOptions.length > 0 ? (
                                <>
                                    {crmOptions.length > 0 ? <CommandSeparator /> : null}
                                    <CommandGroup heading={t('common.company_select.registry', 'From registry — not in CRM')}>
                                        {registryOptions.map((suggestion) => (
                                            <CommandItem
                                                key={suggestion.code}
                                                value={`registry-${suggestion.code}`}
                                                onSelect={() =>
                                                    allowCreate ? openCreateFromRegistry(suggestion) : void importFromRegistry(suggestion)
                                                }
                                                disabled={isImporting}
                                                className="cursor-pointer gap-2"
                                            >
                                                <Building2 className="text-muted-foreground h-4 w-4 shrink-0 opacity-60" />
                                                <div className="flex min-w-0 flex-1 flex-col gap-1 sm:flex-row sm:items-center sm:justify-between sm:gap-2">
                                                    <div className="min-w-0">
                                                        <span className="block truncate text-sm">{suggestion.name}</span>
                                                        {suggestion.address_label ? (
                                                            <span className="text-muted-foreground block truncate text-xs">
                                                                {suggestion.address_label}
                                                            </span>
                                                        ) : null}
                                                    </div>
                                                    <div className="flex shrink-0 items-center gap-1">
                                                        <Badge variant="secondary" className="text-muted-foreground text-xs font-normal">
                                                            {suggestion.code}
                                                        </Badge>
                                                        <Badge
                                                            variant="outline"
                                                            className="border-warning text-warning-subtle-foreground text-xs font-normal"
                                                        >
                                                            {t('common.company_select.not_in_crm', 'Not in CRM')}
                                                        </Badge>
                                                    </div>
                                                </div>
                                            </CommandItem>
                                        ))}
                                    </CommandGroup>
                                </>
                            ) : null}

                            {!isSearching && registryFailed && debouncedSearch.trim().length >= 2 ? (
                                <div className="text-muted-foreground flex items-center gap-2 px-3 py-2 text-xs">
                                    <TriangleAlert className="text-warning-subtle-foreground h-3.5 w-3.5 shrink-0" />
                                    {t('common.company_select.registry_unavailable', 'Registry unavailable — showing CRM results only.')}
                                </div>
                            ) : null}
                        </CommandList>

                        {allowCreate ? (
                            <>
                                <CommandSeparator />
                                <div className="p-1">
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        className="w-full justify-start gap-2 text-sm font-normal"
                                        onPointerDown={(event) => {
                                            event.preventDefault();
                                            setOpen(false);
                                            setCreateRegistryCode(null);
                                            // Let the popover finish unmounting first: closing it restores the
                                            // body's pointer-events on a timer, which would otherwise undo the
                                            // sheet's own lock.
                                            window.setTimeout(() => setIsCreateOpen(true), 0);
                                        }}
                                    >
                                        <Plus className="text-primary h-4 w-4" />
                                        {createLabel}
                                    </Button>
                                </div>
                            </>
                        ) : null}
                    </Command>
                </PopoverContent>
            </Popover>

            {/* A button cannot nest in a button, so the trailing control overlays the
                trigger rather than sitting beside it. */}
            {showClear ? (
                <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={(event) => {
                        event.stopPropagation();
                        clear();
                    }}
                    className="text-muted-foreground hover:text-foreground absolute top-1/2 right-1.5 h-6 w-6 -translate-y-1/2"
                    aria-label={clearLabel}
                >
                    <X className="h-3.5 w-3.5" />
                </Button>
            ) : (
                <ChevronDown className="text-muted-foreground pointer-events-none absolute top-1/2 right-3 h-4 w-4 -translate-y-1/2 opacity-50" />
            )}

            {allowCreate ? (
                <CreateCompanyModal
                    isOpen={isCreateOpen}
                    onClose={closeCreate}
                    onSuccess={handleCompanyCreated}
                    prefillRegistryCode={createRegistryCode}
                />
            ) : null}
        </div>
    );
}
